<template>
  <div class="home-container full-height">
    <div class="grid4">
      <div class="stat-card" v-for="item in statsCards" :key="item.label">
        <h4>{{ item.label }}</h4>
        <div class="stat-value">{{ item.value }}</div>
        <div class="stat-sub">{{ item.tip }}</div>
      </div>
    </div>

    <div class="panel-card home-panel section-gap">
      <h3 class="panel-title">本周接待趋势（近7天）</h3>
      <div class="chart">
        <div ref="trendChartCanvas" class="chart-canvas"></div>
      </div>
    </div>

    <div class="two-col section-gap">
      <div class="rank-col" :class="{ loading: salesLoading }">
        <div class="loading-overlay" v-if="salesLoading"></div>
        <RankingPanel
          :title="salesRankTitle"
          :items="salesRanking"
          :config="salesRankingConfig"
          @action="openPersonnelDetail"
          @filter-change="changeScope"
        />
      </div>
      <div class="rank-col" :class="{ loading: customerRoundsLoading }">
        <div class="loading-overlay" v-if="customerRoundsLoading"></div>
        <RankingPanel
          :title="customerRoundsTitle"
          :items="customerRounds"
          :config="customerRoundsConfig"
          @action="openCustomerConversation"
          @filter-change="changeCustomerScope"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { use, init, graphic } from "echarts/core";
import { LineChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { dashboardApi } from "../../api";
import RankingPanel from "../../components/RankingPanel.vue";

use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

const emptyStats = { today: 0, week: 0, month: 0, online: 0 };
const trendDays = 7;

function buildEmptyStatsCompare() {
  return {
    today: { current: 0, previous: 0, delta: 0, rate: 0, is_increase: true },
    week: { current: 0, previous: 0, delta: 0, rate: 0, is_increase: true },
    month: { current: 0, previous: 0, delta: 0, rate: 0, is_increase: true }
  };
}

function buildEmptyTrend() {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  base.setDate(base.getDate() - (trendDays - 1));

  const items = [];
  for (let i = 0; i < trendDays; i += 1) {
    const day = new Date(base);
    day.setDate(base.getDate() + i);
    const month = String(day.getMonth() + 1).padStart(2, "0");
    const date = String(day.getDate()).padStart(2, "0");
    items.push({ date: `${month}-${date}`, count: 0 });
  }
  return items;
}

export default {
  components: {
    RankingPanel
  },
  data() {
    return {
      scope: "day",
      customerScope: "day",
      scopeName: { day: "今日", week: "本周", month: "本月" },
      stats: { ...emptyStats },
      statsCompare: buildEmptyStatsCompare(),
      trend: buildEmptyTrend(),
      salesRanking: [],
      customerRounds: [],
      salesLoading: false,
      customerRoundsLoading: false,
      trendChart: null,
      overviewCacheMap: {},
      overviewPendingMap: {}
    };
  },
  computed: {
    statsCards() {
      return [
        { label: "今日接待", value: this.stats.today, tip: this.formatCompareTip("today", "昨日") },
        { label: "本周接待", value: this.stats.week, tip: this.formatCompareTip("week", "上周") },
        { label: "本月接待", value: this.stats.month, tip: this.formatCompareTip("month", "上月") },
        { label: "在线人数", value: this.stats.online, tip: "当前在线销售人数" }
      ];
    },
    salesRankTitle() {
      return `销售对话排行榜（${this.scopeName[this.scope] || "今日"}）`;
    },
    customerRoundsTitle() {
      return `客户沟通对话轮数统计（${this.scopeName[this.customerScope] || "今日"}）`;
    },
    salesRankingConfig() {
      return {
        nameKey: "name",
        metricKey: "customer_count",
        metricSuffix: "位客户",
        metricFormatter: (item, value) => {
          const customerCount = Number(value || 0);
          const messageCount = Number(item && item.count ? item.count : 0);
          const manualReplyCount = Number(item && item.manual_reply_count ? item.manual_reply_count : 0);
          const aiReplyCount = Number(item && item.ai_reply_count ? item.ai_reply_count : 0);
          return `${customerCount} 位客户 / ${messageCount} 条消息 / 销售回复 ${manualReplyCount} / AI回复 ${aiReplyCount}`;
        },
        actionKey: "id",
        actionText: "详情",
        rowKey: "id",
        showHeaderPlaceholder: false,
        filters: [
          { label: "今日", value: "day" },
          { label: "本周", value: "week" },
          { label: "本月", value: "month" }
        ],
        activeFilter: this.scope
      };
    },
    customerRoundsConfig() {
      return {
        nameKey: "customer_name",
        metricKey: "rounds",
        metricFormatter: (item, value) => {
          const rounds = Number(value || 0);
          const probability = Number(item && item.deal_probability_score ? item.deal_probability_score : 0);
          const probabilityLabel = item && item.deal_probability_label ? item.deal_probability_label : "低概率";
          return `${rounds} 轮 / 成交概率 ${probability}%（${probabilityLabel}）`;
        },
        actionKey: "id",
        actionText: "详情",
        rowKey: "id",
        showHeaderPlaceholder: false,
        filters: [
          { label: "今日", value: "day" },
          { label: "本周", value: "week" },
          { label: "本月", value: "month" }
        ],
        activeFilter: this.customerScope
      };
    }
  },
  watch: {
    trend: {
      handler() {
        this.$nextTick(() => this.renderTrendChart());
      },
      deep: true
    }
  },
  created() {
    this.loadData();
  },
  mounted() {
    this.initTrendChart();
    window.addEventListener("resize", this.handleWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleWindowResize);
    if (this.trendChart) {
      this.trendChart.dispose();
      this.trendChart = null;
    }
  },
  methods: {
    buildOverviewCacheKey(scope = this.scope, customerScope = this.customerScope) {
      return `${scope}|${customerScope}`;
    },
    getOverviewCache(scope = this.scope, customerScope = this.customerScope) {
      const key = this.buildOverviewCacheKey(scope, customerScope);
      const entry = this.overviewCacheMap[key];
      if (!entry) return null;
      if (entry.expires_at <= Date.now()) {
        this.$delete(this.overviewCacheMap, key);
        return null;
      }
      return entry.data || null;
    },
    setOverviewCache(scope, customerScope, data) {
      const key = this.buildOverviewCacheKey(scope, customerScope);
      this.$set(this.overviewCacheMap, key, {
        data,
        expires_at: Date.now() + 90000
      });
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    resolveErrorMessage(err, fallback = "首页统计加载失败") {
      if (err && typeof err.message === "string" && err.message.trim()) {
        return err.message.trim();
      }
      if (err && typeof err === "object") {
        if (typeof err.message === "string" && err.message.trim()) {
          return err.message.trim();
        }
        if (typeof err.code === "number" && typeof err.message === "string" && err.message.trim()) {
          return err.message.trim();
        }
      }
      return fallback;
    },
    async fetchOverview(scope = this.scope, customerScope = this.customerScope, options = {}) {
      const force = !!(options && options.force);
      const retry = Number(options && options.retry ? options.retry : 0);
      const key = this.buildOverviewCacheKey(scope, customerScope);
      if (!force) {
        const cached = this.getOverviewCache(scope, customerScope);
        if (cached) return cached;
        if (this.overviewPendingMap[key]) return this.overviewPendingMap[key];
      }

      const request = dashboardApi.getOverview({
        scope,
        customer_scope: customerScope
      }).then((data) => {
        this.setOverviewCache(scope, customerScope, data || {});
        return data || {};
      }).catch(async (err) => {
        if (retry > 0) {
          await this.sleep(800);
          return this.fetchOverview(scope, customerScope, {
            force: true,
            retry: retry - 1
          });
        }
        throw err;
      }).finally(() => {
        this.$delete(this.overviewPendingMap, key);
      });

      this.$set(this.overviewPendingMap, key, request);
      return request;
    },
    applyOverviewStats(data) {
      this.stats = data && data.stats ? { ...emptyStats, ...data.stats } : { ...emptyStats };
      this.statsCompare = data && data.stats_compare
        ? { ...buildEmptyStatsCompare(), ...data.stats_compare }
        : buildEmptyStatsCompare();
      this.trend = data && Array.isArray(data.trend) && data.trend.length ? data.trend : buildEmptyTrend();
    },
    initTrendChart() {
      const el = this.$refs.trendChartCanvas;
      if (!el) return;
      if (!this.trendChart) {
        this.trendChart = init(el);
      }
      this.renderTrendChart();
    },
    renderTrendChart() {
      if (!this.trendChart) return;

      const labels = this.trend.map((item) => item.date);
      const values = this.trend.map((item) => Number(item.count) || 0);
      const max = Math.max(10, ...values);
      const yMax = Math.ceil(max / 5) * 5;

      this.trendChart.setOption(
        {
          animationDuration: 500,
          animationEasing: "cubicOut",
          grid: { left: 42, right: 16, top: 20, bottom: 34 },
          tooltip: {
            trigger: "axis",
            confine: true,
            backgroundColor: "rgba(15, 23, 42, 0.9)",
            borderWidth: 0,
            textStyle: { color: "#fff" },
            formatter(params) {
              const point = Array.isArray(params) ? params[0] : params;
              return `${point.axisValue}<br/>接待客户：${point.data || 0}`;
            }
          },
          xAxis: {
            type: "category",
            boundaryGap: false,
            data: labels,
            axisLine: { lineStyle: { color: "#d5e4df" } },
            axisTick: { show: false },
            axisLabel: { color: "#64748b", fontSize: 11 }
          },
          yAxis: {
            type: "value",
            min: 0,
            max: yMax,
            splitNumber: 5,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: "#64748b", fontSize: 11 },
            splitLine: { lineStyle: { color: "#e2ebe8", type: "dashed" } }
          },
          series: [
            {
              type: "line",
              smooth: true,
              data: values,
              symbol: "circle",
              symbolSize: 7,
              showSymbol: true,
              lineStyle: {
                width: 3,
                color: new graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: "#0f766e" },
                  { offset: 1, color: "#0ea5e9" }
                ])
              },
              itemStyle: {
                color: "#0f766e",
                borderColor: "#fff",
                borderWidth: 2
              },
              areaStyle: {
                color: new graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "rgba(20, 184, 166, 0.30)" },
                  { offset: 1, color: "rgba(20, 184, 166, 0.03)" }
                ])
              }
            }
          ]
        },
        true
      );
    },
    handleWindowResize() {
      if (this.trendChart) {
        this.trendChart.resize();
      }
    },
    changeScope(scope) {
      if (!scope || scope === this.scope) return;
      this.scope = scope;
      this.loadSalesRanking();
    },
    changeCustomerScope(scope) {
      if (!scope || scope === this.customerScope) return;
      this.customerScope = scope;
      this.loadCustomerRounds();
    },
    formatCompareTip(period, baseLabel) {
      const item = this.statsCompare && this.statsCompare[period];
      if (!item) return `较${baseLabel} 持平`;

      const delta = Number(item.delta || 0);
      const rateValue = Number(item.rate || 0);
      const absDelta = Math.abs(delta);
      const absRate = Math.abs(rateValue).toFixed(1);

      if (delta === 0) return `较${baseLabel} 持平`;
      if (delta > 0) return `较${baseLabel} 增加${absDelta}（${absRate}%）`;
      return `较${baseLabel} 减少${absDelta}（${absRate}%）`;
    },
    openPersonnelDetail(item) {
      if (!item || !item.id) return;
      const salesName = ((item && item.name) || "").trim();
      if (!salesName || salesName === "-") return;
      this.$router.push({
        path: "/customers",
        query: { sales_name: salesName }
      });
    },
    openCustomerConversation(item) {
      if (!item || !item.id) return;
      this.$router.push({
        path: `/customers/${item.id}/conversation`,
        query: { name: item.customer_name || "" }
      });
    },
    async loadData() {
      try {
        const data = await this.fetchOverview(this.scope, this.customerScope, { retry: 1 });
        this.applyOverviewStats(data);
        this.salesRanking = data && Array.isArray(data.sales_ranking) ? data.sales_ranking : [];
        this.customerRounds = data && Array.isArray(data.customer_rounds_ranking)
          ? data.customer_rounds_ranking
          : [];
      } catch (e) {
        this.stats = { ...emptyStats };
        this.statsCompare = buildEmptyStatsCompare();
        this.trend = buildEmptyTrend();
        this.salesRanking = [];
        this.customerRounds = [];
        this.$message.error(this.resolveErrorMessage(e, "首页统计加载失败"));
      }
      this.$nextTick(() => {
        this.initTrendChart();
        this.handleWindowResize();
      });
    },
    async loadSalesRanking() {
      this.salesLoading = true;
      try {
        const data = await this.fetchOverview(this.scope, this.customerScope, { retry: 1 });
        this.applyOverviewStats(data);
        this.salesRanking = data && Array.isArray(data.sales_ranking) ? data.sales_ranking : [];
      } catch (e) {
        this.salesRanking = [];
        this.$message.error(this.resolveErrorMessage(e, "销售排行榜加载失败"));
      } finally {
        this.salesLoading = false;
      }
    },
    async loadCustomerRounds() {
      this.customerRoundsLoading = true;
      try {
        const data = await this.fetchOverview(this.scope, this.customerScope, { retry: 1 });
        this.applyOverviewStats(data);
        this.customerRounds = data && Array.isArray(data.customer_rounds_ranking)
          ? data.customer_rounds_ranking
          : [];
      } catch (e) {
        this.customerRounds = [];
        this.$message.error(this.resolveErrorMessage(e, "客户轮次统计加载失败"));
      } finally {
        this.customerRoundsLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.grid4 {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 11px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.87);
  border: 1px solid #e8efec;
  border-radius: 14px;
  padding: 13px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}

.stat-card h4 {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
}

.stat-value {
  font-size: 30px;
  font-weight: 700;
  margin: 7px 0 4px;
  color: #0b4f49;
  letter-spacing: 0.02em;
}

.stat-sub {
  font-size: 12px;
  color: #0f766e;
}

.panel-title {
  margin: 0 0 9px;
  font-size: 15px;
}

.home-panel:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}

.chart {
  height: 262px;
  border: 1px solid #dceae5;
  border-radius: 14px;
  background:
    radial-gradient(900px 220px at 8% -30%, rgba(20, 184, 166, 0.12) 0, transparent 55%),
    radial-gradient(700px 220px at 100% 120%, rgba(14, 165, 233, 0.1) 0, transparent 60%),
    linear-gradient(180deg, #fbfffe, #f1f8f6);
  padding: 9px;
  position: relative;
}

.chart-canvas {
  width: 100%;
  height: 100%;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-items: stretch;
  grid-auto-rows: 1fr;
}

.rank-col {
  position: relative;
  min-width: 0;
}

.loading {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
}

.loading-overlay::after {
  content: "";
  width: 32px;
  height: 32px;
  border: 3px solid rgba(15, 118, 110, 0.1);
  border-top-color: #0f766e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stat-card {
  position: relative;
}

.stat-card .loading-overlay {
  border-radius: 14px;
}

.home-panel .loading-overlay {
  border-radius: 14px;
}

.two-col {
  position: relative;
}

.two-col .loading-overlay {
  border-radius: 14px;
}

.full-height {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 130px);
}

.home-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-gap {
  margin: 12px 0;
}

.two-col {
  flex: 1;
  min-height: 250px;
  overflow: hidden;
}

@media (max-width: 1100px) {
  .grid4 {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .two-col {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }
}
</style>
